<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col">
        <div
          class="row"
          v-if="isAddOrEdit"
        >
          <div class="col-lg-8 offset-lg-2">
            <form @submit.prevent="saveCompany(company)">
              <div class="card mt-0">
                <div class="card-header">
                  <span v-if="company.id">
                    {{ $t("gpool.edit_company") }}: {{ company.name }}
                  </span>
                  <span v-else>{{ $t("gpool.create_company") }}</span>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <div class="row">
                      <div class="col">
                        <label
                          class="form-label"
                          for="name"
                        >
                          {{ $t("gpool.form_name") }}
                        </label>
                        <input
                          class="form-control"
                          type="text"
                          name="name"
                          id="name"
                          :disabled="posting"
                          v-model="company.name"
                          required
                        />
                      </div>
                      <div class="col">
                        <label
                          class="form-label"
                          for="category_id"
                        >
                          {{ $t("gpool.form_category") }}
                        </label>
                        <select
                          name="category_id"
                          id="category_id"
                          class="form-select"
                          v-model="company.category_id"
                          required
                        >
                          <option
                            value=""
                            selected
                            disabled
                          >
                            {{ $t("gpool.form_select_category") }}
                          </option>
                          <option
                            v-for="category in categories"
                            :key="category.id"
                            :value="category.id"
                          >
                            {{ category.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="mb-3">
                    <div class="row">
                      <div class="col">
                        <label
                          for="prefix"
                          class="form-label"
                        >
                          {{ $t("gpool.form_prefix") }}
                        </label>
                        <input
                          class="form-control"
                          type="text"
                          name="prefix"
                          id="prefix"
                          :disabled="posting"
                          v-model="company.prefix"
                          required
                        />
                      </div>
                      <div class="col">
                        <label
                          for="logo"
                          class="form-label"
                        >
                          {{ $t("gpool.form_logo") }}
                        </label>
                        <input
                          type="file"
                          @change="onFileChange"
                          name="logo"
                          id="logo"
                          :disabled="posting"
                          class="form-control"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-footer">
                  <button
                    :disabled="posting"
                    type="submit"
                    class="btn"
                    :class="{
                      'btn-success': !company.id,
                      'btn-primary': company.id
                    }"
                  >
                    <i
                      v-if="posting"
                      class="fa fa-spinner fa-spin"
                    ></i>
                    <template v-else>
                      <span v-if="company.id">
                        {{ $t("gpool.form_update") }}
                      </span>
                      <span v-else>
                        {{ $t("gpool.form_add") }}
                      </span>
                    </template>
                  </button>
                  <button
                    class="btn btn-link"
                    @click="setIsAddOrEdit(false)"
                  >
                    {{ $t("gpool.form_cancel") }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div
          class="position-relative card mt-0"
          v-else
        >
          <div class="card-header">
            <div class="align-items-center row">
              <div class="col-6">
                <div class="mb-0 card-title h5">
                  {{ $t("gpool.menu_companies") }}
                </div>
              </div>
              <div
                class="text-end col-6"
                @click="setIsAddOrEdit(true)"
              >
                <button
                  type="button"
                  class="btn btn-primary"
                >
                  <i class="fa fa-plus"></i>
                  <span class="ms-2 align-middle">{{
                    $t("gpool.create_company")
                  }}</span>
                </button>
              </div>
            </div>
          </div>
          <div
            class="rounded-0 position-absolute w-100 progress"
            style="height: 6px"
            v-if="loading"
          >
            <div
              role="progressbar"
              class="progress-bar progress-bar-animated progress-bar-striped"
              aria-valuenow="100"
              aria-valuemin="0"
              aria-valuemax="100"
              style="width: 100%"
            ></div>
          </div>
          <div
            v-else-if="!companies.length"
            class="text-center p-4"
          >
            {{ $t("gpool.no_data") }}
          </div>
          <table
            v-else
            class="table table-striped table-bordered"
          >
            <thead>
              <tr>
                <th>ID</th>
                <th class="text-center">{{ $t("gpool.table_logo") }}</th>
                <th width="100%">{{ $t("gpool.table_name") }}</th>
                <th class="text-center">{{ $t("gpool.table_category") }}</th>
                <th class="text-center">{{ $t("gpool.table_prefix") }}</th>
                <th class="text-center">{{ $t("gpool.table_order") }}</th>
                <th class="text-center">{{ $t("gpool.table_status") }}</th>
                <th class="text-center">{{ $t("gpool.table_actions") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="company in companies">
                <td>{{ company.id }}</td>
                <td>
                  <img
                    style="max-height: 50px; max-width: 50px"
                    :src="company.logo_url"
                  />
                </td>
                <td>{{ company.name }}</td>
                <td>{{ getCategoryName(company.category_id) }}</td>
                <td>{{ company.prefix }}</td>
                <td>
                  <input
                    type="text"
                    class="form-control text-center form-control-sm"
                    v-model="company.order"
                    @blur="updateOrder(company)"
                  />
                  <!-- <div
                    role="group"
                    class="btn-group"
                  >
                    <button
                      type="button"
                      class="btn"
                      :disabled="company.order == 1"
                      @click="moveUp(company.id)"
                    >
                      <i class="fa fa-arrow-up"></i>
                    </button>
                    <button
                      type="button"
                      class="btn"
                      :disabled="company.order == companies.length"
                      @click="moveDown(company.id)"
                    >
                      <i class="fa fa-arrow-down"></i>
                    </button>
                  </div> -->
                </td>
                <td class="text-center">
                  <div
                    class="form-switch"
                    @click="toggleStatus(company)"
                  >
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="status"
                      style="pointer-events: none"
                      :disabled="statusing"
                      :checked="company.status"
                    />
                  </div>
                </td>
                <td class="text-center">
                  <div
                    role="group"
                    class="btn-group"
                  >
                    <button
                      type="button"
                      class="btn"
                      @click="editCompany(company.id)"
                    >
                      <i class="fa fa-pencil"></i>
                    </button>
                    <button
                      type="button"
                      class="btn"
                      @click="deleteCompany(company.id)"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <!-- <div class="card-footer"></div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
export default {
  name: "Companies",
  computed: {
    ...mapGetters("category", ["categories"]),
    ...mapGetters("company", [
      "companies",
      "company",
      "loading",
      "posting",
      "isAddOrEdit",
      "statusing"
    ])
  },
  beforeDestroy() {
    this.setLoading(true);
    this.setIsAddOrEdit(false);
  },
  async mounted() {
    await this.getCategories({ bypass: true });
    this.getCompanies();
  },
  methods: {
    onFileChange(e) {
      const file = e.target.files[0];
      this.company.logo = file;
    },
    getCategoryName(id) {
      const category = this.categories.find(category => category.id === id);
      return category ? category.name : "";
    },
    ...mapActions("company", [
      "getCompanies",
      "saveCompany",
      "setIsAddOrEdit",
      "toggleStatus",
      "editCompany",
      "deleteCompany",
      "updateOrder",
      "setLoading"
    ]),
    ...mapActions("category", ["getCategories"])
  }
};
</script>



// WEBPACK FOOTER //
// src/pages/Companies.vue